import { Box, IconButton, InputBase, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { normalizeContainerNo } from '../../../lib/packingListAllocation';

const BORDER = '1px solid #e0e0e0';
const ROW_HEIGHT = 28;
/** Line Items 그리드와 같은 모양(머리 회색, 11px, 칸 테두리)으로 맞춘 표. */
const tableSx = {
    border: BORDER,
    tableLayout: 'fixed',
    '& .MuiTableCell-root': { p: 0, height: ROW_HEIGHT, fontSize: 11, borderBottom: BORDER, borderRight: BORDER },
    '& .MuiTableCell-root:last-of-type': { borderRight: 'none' },
    '& .MuiTableCell-head': { bgcolor: '#f1f1f1', color: 'text.secondary', fontWeight: 500, textAlign: 'center', height: 26 },
    '& .MuiInputBase-root': { width: '100%', height: ROW_HEIGHT - 1, fontSize: 11 },
    '& .MuiInputBase-input': { px: 1, py: 0, fontFamily: 'monospace' },
    '& .MuiTableCell-body:focus-within': { outline: '1px solid #00647e', outlineOffset: -1 },
};

/**
 * PL 하단에 적힌 컨테이너 번호 목록. 한 줄에 번호 하나씩 표로 두고, 칸이 곧 입력칸이라 누르면 바로 고친다.
 * 파싱이 잘못 읽은 번호는 고치거나 지우고, B/L에 없는 번호가 남아 있으면 확정할 수 없으므로 그 줄을 오류로 표시한다.
 * readOnly면 번호를 고치거나 더하고 지울 수 없다.
 */
export default function DocContainerNumbersField({ numbers, onChange, blNumbers, highlight, readOnly = false }) {
    const onBl = (number) => blNumbers.some((n) => normalizeContainerNo(n) === normalizeContainerNo(number));
    const update = (index, value) => onChange(numbers.map((n, i) => (i === index ? value : n)));

    return (
        <Box id="header-container" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, borderRadius: 1, outline: highlight ? '2px solid #d92936' : 'none', outlineOffset: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#003a52', flexShrink: 0 }}>Containers on PL</Typography>
                {!readOnly && (
                    <IconButton size="small" title="Add container" onClick={() => onChange([...numbers, ''])} sx={{ ml: 'auto', p: '2px' }}>
                        <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Box>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -0.5, wordBreak: 'break-word' }}>B/L: {blNumbers.join(', ') || 'none'}</Typography>
            <Table size="small" sx={tableSx}>
                <colgroup>
                    <col style={{ width: 28 }} />
                    <col />
                    <col style={{ width: 76 }} />
                    <col style={{ width: 28 }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Container No.</TableCell>
                        <TableCell>B/L</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {numbers.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.disabled' }}>No container numbers on this PL.</TableCell>
                        </TableRow>
                    )}
                    {numbers.map((number, index) => {
                        const filled = Boolean(number.trim());
                        const mismatch = filled && !onBl(number);
                        return (
                            <TableRow key={index} sx={mismatch ? { bgcolor: 'rgba(217,41,54,0.08)' } : undefined}>
                                <TableCell align="center" sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                                <TableCell>
                                    <InputBase value={number} onChange={(e) => update(index, e.target.value)} readOnly={readOnly} error={mismatch} placeholder="Container No." inputProps={{ 'aria-label': `Container on PL ${index + 1}` }} />
                                </TableCell>
                                <TableCell align="center" sx={{ color: mismatch ? 'error.main' : 'success.main', fontWeight: mismatch ? 600 : 400 }}>{!filled ? '' : mismatch ? 'Not on B/L' : 'On B/L'}</TableCell>
                                <TableCell align="center">
                                    {!readOnly && (
                                        <IconButton size="small" title="Remove container" onClick={() => onChange(numbers.filter((_, i) => i !== index))} sx={{ p: '2px' }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}
